import React from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Chip,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  SmartToy as SmartToyIcon,
  Person as PersonIcon,
  Chat as ChatIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { PopularExpert } from '../../types/admin';

interface ExpertRankingTableProps {
  data: PopularExpert[];
  title?: string;
  maxRows?: number;
}

export const ExpertRankingTable: React.FC<ExpertRankingTableProps> = ({
  data,
  title = 'AI専門家人気ランキング',
  maxRows = 10,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  // セッション時間を分:秒形式に変換
  const formatSessionLength = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // ランキング順位の色を取得
  const getRankColor = (index: number): string => {
    switch (index) {
      case 0: return '#FFD700'; // Gold
      case 1: return '#C0C0C0'; // Silver  
      case 2: return '#CD7F32'; // Bronze
      default: return '#E0E0E0'; // Default
    }
  };

  // 表示するデータをmaxRowsで制限
  const displayData = data.slice(0, maxRows);

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography 
        variant="h6" 
        gutterBottom 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          fontSize: { xs: '1.1rem', sm: '1.25rem' },
        }}
      >
        <SmartToyIcon color="primary" sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
        {title}
      </Typography>
      
      <TableContainer sx={{ 
        overflowX: 'auto',
        '& .MuiTable-root': {
          minWidth: { xs: 600, sm: 650 },
        }
      }}>
        <Table size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ 
                minWidth: 60,
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                fontWeight: 600,
              }}>
                順位
              </TableCell>
              <TableCell sx={{ 
                minWidth: { xs: 120, sm: 140 },
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                fontWeight: 600,
              }}>
                AI専門家
              </TableCell>
              <TableCell align="center" sx={{ 
                minWidth: { xs: 80, sm: 100 },
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                fontWeight: 600,
              }}>
                利用回数
              </TableCell>
              {!isSmall && (
                <TableCell align="center" sx={{ 
                  minWidth: 80,
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  fontWeight: 600,
                }}>
                  ユーザー数
                </TableCell>
              )}
              <TableCell align="center" sx={{ 
                minWidth: { xs: 80, sm: 100 },
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                fontWeight: 600,
              }}>
                セッション数
              </TableCell>
              {!isSmall && (
                <TableCell align="center" sx={{ 
                  minWidth: 90,
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  fontWeight: 600,
                }}>
                  平均時間
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayData.map((expert, index) => (
              <TableRow 
                key={expert.expertId}
                sx={{ 
                  '&:hover': { backgroundColor: 'action.hover' },
                  backgroundColor: index < 3 ? `${getRankColor(index)}15` : 'inherit'
                }}
              >
                {/* 順位 */}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                      sx={{
                        width: { xs: 20, sm: 24 },
                        height: { xs: 20, sm: 24 },
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        bgcolor: getRankColor(index),
                        color: index < 3 ? '#000' : '#666'
                      }}
                    >
                      {index + 1}
                    </Avatar>
                  </Box>
                </TableCell>
                
                {/* AI専門家名 */}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SmartToyIcon sx={{ 
                      fontSize: { xs: 14, sm: 16 }, 
                      color: 'primary.main' 
                    }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: index < 3 ? 'bold' : 'normal',
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        wordBreak: 'break-word',
                      }}
                    >
                      {expert.expertName}
                    </Typography>
                  </Box>
                </TableCell>
                
                {/* 利用回数 */}
                <TableCell align="center">
                  <Chip
                    icon={<ChatIcon sx={{ fontSize: { xs: '12px !important', sm: '14px !important' } }} />}
                    label={expert.usageCount}
                    size="small"
                    color={index === 0 ? 'primary' : 'default'}
                    variant={index < 3 ? 'filled' : 'outlined'}
                    sx={{
                      height: { xs: 20, sm: 24 },
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      '& .MuiChip-label': {
                        px: { xs: 0.5, sm: 1 },
                      }
                    }}
                  />
                </TableCell>
                
                {/* ユニークユーザー数 - スマホでは非表示 */}
                {!isSmall && (
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        {expert.uniqueUsers}
                      </Typography>
                    </Box>
                  </TableCell>
                )}
                
                {/* 総セッション数 */}
                <TableCell align="center">
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    {expert.totalSessions}
                  </Typography>
                </TableCell>
                
                {/* 平均セッション時間 - スマホでは非表示 */}
                {!isSmall && (
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <TimerIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        {formatSessionLength(expert.averageSessionLength)}
                      </Typography>
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* スマホサイズでの省略された情報の説明 */}
      {isSmall && data.length > 0 && (
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ display: 'block', mt: 1, fontSize: '0.7rem' }}
        >
          ※ スマホ表示では一部項目を省略しています
        </Typography>
      )}
      
      {data.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
          >
            データがありません
          </Typography>
        </Box>
      )}
      
      {data.length > maxRows && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
          >
            上位{maxRows}件を表示中（全{data.length}件）
          </Typography>
        </Box>
      )}
    </Paper>
  );
}; 